import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  TextField,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { 
  doc, 
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import ProviderCommissions from '../components/ProviderCommissions';
import CommunicationTemplates from './CommunicationTemplates';

/**
 * @param {object} [props.source] another country's settings (utils/catalogSources.js indiaCatalog);
 *   without it the page reads and saves the Australian settings as before.
 */
const PlatformSettings = ({ source = null }) => {
  // Fields that only apply to Australia (Stripe, discounts, refunds, templates).
  const auOnly = !source;
  const cur = source ? source.currencySymbol : '$';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentTab, setCurrentTab] = useState(0);

  // Settings state
  const [settings, setSettings] = useState(source ? source.defaultPlatformSettings : {
    // Commission Settings
    defaultCommissionRate: 20,
    commissionType: 'percentage', // 'percentage' or 'fixed'
    platformFee: 5,
    platformFeeType: 'fixed', // 'percentage' or 'fixed'
    
    // Booking Settings
    enableDiscounts: true,
    maxDiscountPercentage: 50,
    providerAcceptanceWindowMinutes: 60,
    customerRefundWindowHours: 24,
    
    // Payment Settings
    currency: 'USD',
    taxRate: 0,
    // Stripe settings (publishable keys)
    stripeMode: 'test', // 'test' or 'live'
    stripePublishableKeyTest: '',
    stripePublishableKeyLive: '',
    
    // Notification Settings
    sendBookingConfirmation: true,
    sendPaymentReceipt: true,
    
    // Service Provider Settings
    minimumPayout: 50,
    payoutFrequency: 'weekly' // 'daily', 'weekly', 'monthly'
  });

  // Fetch platform settings from Firestore
  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (source) {
        const data = await source.getPlatformSettings();
        if (data) setSettings((prevSettings) => ({ ...prevSettings, ...data }));
        return;
      }

      const docRef = doc(db, 'platformSettings', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...docSnap.data()
        }));
      }
    } catch (err) {
      console.error('Error fetching platform settings:', err);
      setError('Failed to fetch platform settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle number input change
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    setSettings(prev => ({
      ...prev,
      [name]: numValue
    }));
  };

  // Save settings
  const handleSave = async () => {
    try {
      setSaving(true);

      if (source) {
        const saved = await source.savePlatformSettings(settings);
        setSettings((prev) => ({ ...prev, ...saved }));
        showSnackbar('Platform settings saved successfully', 'success');
        return;
      }
      
      const docRef = doc(db, 'platformSettings', 'main');
      await setDoc(docRef, {
        ...settings,
        updatedAt: new Date()
      }, { merge: true });
      
      showSnackbar('Platform settings saved successfully', 'success');
    } catch (err) {
      console.error('Error saving platform settings:', err);
      showSnackbar(source && err?.message ? err.message : 'Failed to save platform settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    fetchSettings();
    showSnackbar('Settings reset to last saved values', 'info');
  };

  // Calculate earnings example (per service)
  const calculateEarningsExample = (servicePrice) => {
    const commission = settings.commissionType === 'percentage' 
      ? (servicePrice * settings.defaultCommissionRate / 100)
      : settings.defaultCommissionRate;
    
    const platformFee = settings.platformFeeType === 'percentage'
      ? (servicePrice * settings.platformFee / 100)
      : settings.platformFee;
    
    const providerEarnings = servicePrice - commission - platformFee;
    
    return {
      servicePrice,
      commission,
      platformFee,
      providerEarnings
    };
  };

  // Calculate order example with multiple services
  const calculateOrderExample = () => {
    const service1 = 100;
    const service2 = 50;
    const orderTotal = service1 + service2;
    
    const commission1 = settings.commissionType === 'percentage' 
      ? (service1 * settings.defaultCommissionRate / 100)
      : settings.defaultCommissionRate;
    const commission2 = settings.commissionType === 'percentage' 
      ? (service2 * settings.defaultCommissionRate / 100)
      : settings.defaultCommissionRate;
    
    const totalCommission = commission1 + commission2;
    
    // Platform fee is charged ONCE per order
    const platformFee = settings.platformFeeType === 'percentage'
      ? (orderTotal * settings.platformFee / 100)
      : settings.platformFee;
    
    const providerEarnings = orderTotal - totalCommission - platformFee;
    
    return {
      orderTotal,
      totalCommission,
      platformFee,
      providerEarnings
    };
  };

  const examplePrice = 100;
  const earningsBreakdown = calculateEarningsExample(examplePrice);

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  if (loading) {
    return (
      <Box>
        <Typography>Loading platform settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="General Settings" />
        {auOnly && <Tab label="Provider Commissions" />}
        {auOnly && <Tab label="Email Templates" />}
      </Tabs>

      {currentTab === 0 && (
        <Box>

      <Grid container spacing={3}>
        {/* Commission Settings */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commission Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Provider Acceptance Window (minutes)"
                  name="providerAcceptanceWindowMinutes"
                  type="number"
                  value={settings.providerAcceptanceWindowMinutes}
                  onChange={handleNumberChange}
                  fullWidth
                  helperText="Time providers have to accept a booking (minutes)"
                />

                {auOnly && (
                <TextField
                  label="Customer Refund Window (hours)"
                  name="customerRefundWindowHours"
                  type="number"
                  value={settings.customerRefundWindowHours}
                  onChange={handleNumberChange}
                  fullWidth
                  helperText="Within this many hours, refunds follow the 'within window' policy"
                />
                )}

                <TextField
                  select
                  label="Commission Type"
                  name="commissionType"
                  value={settings.commissionType}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ({cur})</option>
                </TextField>

                <TextField
                  label="Default Commission Rate"
                  name="defaultCommissionRate"
                  type="number"
                  value={settings.defaultCommissionRate}
                  onChange={handleNumberChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {settings.commissionType === 'percentage' ? '%' : cur}
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    settings.commissionType === 'percentage'
                      ? 'Percentage deducted from service price'
                      : 'Fixed amount deducted from service price'
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Fee Settings */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Platform Fee Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  select
                  label="Platform Fee Type"
                  name="platformFeeType"
                  value={settings.platformFeeType}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!auOnly}
                  helperText={auOnly ? undefined : 'Bookings charge the platform fee as a fixed amount per order'}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {auOnly && <option value="percentage">Percentage (%)</option>}
                  <option value="fixed">Fixed Amount ({cur})</option>
                </TextField>

                <TextField
                  label="Platform Fee"
                  name="platformFee"
                  type="number"
                  value={settings.platformFee}
                  onChange={handleNumberChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {settings.platformFeeType === 'percentage' ? '%' : cur}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Fee charged once per order (not per service)"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Earnings Calculator Example - Single Service */}
        <Grid item size={{ xs: 12, md: 12 }}>
          <Card sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Single Service Example (Service Price: {cur}{examplePrice})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Service Price
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {cur}{earningsBreakdown.servicePrice.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Commission
                    </Typography>
                    <Typography variant="h5" color="error">
                      -{cur}{earningsBreakdown.commission.toFixed(2)}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      ({settings.defaultCommissionRate}{settings.commissionType === 'percentage' ? '%' : cur})
                    </Typography> */}
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Platform Fee
                    </Typography>
                    <Typography variant="h5" color="error">
                      -{cur}{earningsBreakdown.platformFee.toFixed(2)}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      ({settings.platformFee}{settings.platformFeeType === 'percentage' ? '%' : cur})
                    </Typography> */}
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Provider
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {cur}{earningsBreakdown.providerEarnings.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Multi-Service Order Example */}
        <Grid item size={{ xs: 12, md: 12 }}>
          <Card sx={{ backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order with Multiple Services (Service 1: {cur}100 + Service 2: {cur}50)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                💡 Platform fee is charged ONCE per order, not per service
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Total
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {cur}{calculateOrderExample().orderTotal.toFixed(2)}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      (2 services)
                    </Typography> */}
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Commission
                    </Typography>
                    <Typography variant="h5" color="error">
                      -{cur}{calculateOrderExample().totalCommission.toFixed(2)}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      ({settings.defaultCommissionRate}{settings.commissionType === 'percentage' ? '%' : cur} per service)
                    </Typography> */}
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Platform Fee
                    </Typography>
                    <Typography variant="h5" color="error">
                      -{cur}{calculateOrderExample().platformFee.toFixed(2)}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      (once per order)
                    </Typography> */}
                  </Box>
                </Grid>
                
                <Grid item size={{ xs: 12, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Provider Earnings
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {cur}{calculateOrderExample().providerEarnings.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Discount Settings */}
        {auOnly && (
        <Grid item size={{ xs: 12, md: 3 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Discount Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableDiscounts}
                      onChange={handleInputChange}
                      name="enableDiscounts"
                    />
                  }
                  label="Enable Discounts"
                />

                <TextField
                  label="Maximum Discount Percentage"
                  name="maxDiscountPercentage"
                  type="number"
                  value={settings.maxDiscountPercentage}
                  onChange={handleNumberChange}
                  disabled={!settings.enableDiscounts}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText={settings.enableDiscounts ? "Maximum discount that can be applied to services" : "Enable discounts to set maximum percentage"}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Payment Settings */}
        {auOnly && (
        <Grid item size={{ xs: 12, md: 3 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Payment Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  select
                  label="Currency"
                  name="currency"
                  value={settings.currency}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </TextField>

                <TextField
                  label="Tax Rate"
                  name="taxRate"
                  type="number"
                  value={settings.taxRate}
                  onChange={handleNumberChange}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Tax rate applied to transactions (if applicable)"
                />
                
                <TextField
                  label="Stripe Mode"
                  name="stripeMode"
                  select
                  value={settings.stripeMode}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{ native: true }}
                  helperText="Choose 'live' to use live publishable key"
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </TextField>

                <TextField
                  label="Stripe Publishable Key (Test)"
                  name="stripePublishableKeyTest"
                  type="text"
                  value={settings.stripePublishableKeyTest || ''}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="Publishable key for Stripe test mode (pk_test_...) - do not commit to git"
                />

                <TextField
                  label="Stripe Publishable Key (Live)"
                  name="stripePublishableKeyLive"
                  type="text"
                  value={settings.stripePublishableKeyLive || ''}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="Publishable key for Stripe live mode (pk_live_...) - do not commit to git"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Service Provider Payout Settings */}
        <Grid item size={{ xs: 12, md: 3 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Service Provider Payout Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Minimum Payout Amount"
                  name="minimumPayout"
                  type="number"
                  value={settings.minimumPayout}
                  onChange={handleNumberChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{cur}</InputAdornment>,
                  }}
                  helperText="Minimum balance required for payout"
                />

                <TextField
                  select
                  label="Payout Frequency"
                  name="payoutFrequency"
                  value={settings.payoutFrequency}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </TextField>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        {auOnly && (
        <Grid item size={{ xs: 12, md: 3 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendBookingConfirmation}
                      onChange={handleInputChange}
                      name="sendBookingConfirmation"
                    />
                  }
                  label="Send Booking Confirmation"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendPaymentReceipt}
                      onChange={handleInputChange}
                      name="sendPaymentReceipt"
                    />
                  }
                  label="Send Payment Receipt"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>
      </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',mt:3, mb: 3 }}>
        <div>
          {/* <Typography variant="h4" gutterBottom>
            Platform Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure commission rates, fees, and platform-wide settings
          </Typography> */}
        </div>
        {currentTab === 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Box>

      {currentTab === 1 && (
        <Box>
          <ProviderCommissions />
        </Box>
      )}

      {currentTab === 2 && (
        <Box>
          <CommunicationTemplates embedded />
        </Box>
      )}

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformSettings;
